/**
 * Fleet dashboard data (TMMPDM-0020): GET /v1/admin/dashboard with optional resellerId.
 * Money is one row per currency from the server — never joined or summed client-side.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@/api/tmm/admin';
import type { FleetDashboard, TenantView } from '@/api/tmm/types';
import type { Period } from './period';

/** Size callers pass to `listTenants` when they want "all tenants" for a slug join. */
export const TENANT_JOIN_PAGE_SIZE = 200;

export interface FleetData {
  dashboard: FleetDashboard;
  tenantsBySlug: Map<string, TenantView>;
  /** When this load arrived — the client clock; the API publishes no as-of (TMMPDM-0035). */
  loadedAt: Date;
}

export interface UseFleetDashboard {
  data: FleetData | null;
  loading: boolean;
  error: boolean;
  /** The failure behind `error`, for `ErrorState`'s reference id. */
  failure: unknown;
  refetch: () => void;
}

export function useFleetDashboard(period: Period, resellerId?: number): UseFleetDashboard {
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<{
    key: string;
    data: FleetData | null;
    error: boolean;
    failure?: unknown;
  }>({
    key: '',
    data: null,
    error: false,
  });
  const key = `${period.from}:${period.to}:${resellerId ?? ''}:${reloadToken}`;

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminApi.dashboard({
        from: period.from,
        to: period.to,
        ...(resellerId !== undefined ? { resellerId } : {}),
      }),
      adminApi.listTenants({ size: TENANT_JOIN_PAGE_SIZE }),
    ])
      .then(([dashboard, list]) => {
        if (cancelled) return;
        const tenantsBySlug = new Map(list.tenants.map((t) => [t.slug, t]));
        setState({ key, data: { dashboard, tenantsBySlug, loadedAt: new Date() }, error: false });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setState({ key, data: null, error: true, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [key, period.from, period.to, resellerId]);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);
  const current = state.key === key;
  return {
    data: current ? state.data : null,
    loading: !current,
    error: current && state.error,
    failure: current ? state.failure : undefined,
    refetch,
  };
}
